import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, lastValueFrom, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  OutlookMailProcessingResponse,
  OutlookMailboxesResponse,
  OutlookMailboxOption,
  OutlookOnboardingStatusResponse,
  OutlookRejectMailResponse,
  OutlookUnsubscribeResponse,
  OutlookWebhookStatusResponse,
} from '../models/outlook-mailbox.model';

@Injectable({
  providedIn: 'root',
})
export class OutlookMailboxService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.outlookApiBaseUrl;

  async getOnboardingStatus(): Promise<OutlookOnboardingStatusResponse> {
    const url = `${this.baseUrl}/mail-assistent/status`;
    return lastValueFrom(
      this.http.get<OutlookOnboardingStatusResponse>(url).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  async listMailboxes(officeSsoJwt: string): Promise<OutlookMailboxesResponse> {
    const url = `${this.baseUrl}/mail-assistent/mailboxes`;
    const headers = new HttpHeaders({ 'X-Office-Token': officeSsoJwt });
    return lastValueFrom(
      this.http.get<OutlookMailboxesResponse>(url, { headers }).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    ).then((body) => ({
      mailboxes: body.mailboxes ?? [],
    }));
  }

  async activateMailboxes(
    officeSsoJwt: string,
    mailboxes: Array<{ graph_user_id: string; type: 'primary' }>,
  ): Promise<void> {
    const url = `${this.baseUrl}/mail-assistent/mailboxes/activate`;
    const headers = new HttpHeaders({ 'X-Office-Token': officeSsoJwt });
    await lastValueFrom(
      this.http.post<{ activated: number; message: string }>(url, { mailboxes }, { headers }).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  async getWebhookStatus(): Promise<OutlookWebhookStatusResponse> {
    const url = `${this.baseUrl}/mail-assistent/webhook-status`;
    return lastValueFrom(
      this.http.get<OutlookWebhookStatusResponse>(url).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  async getCurrentMailProcessingStatus(mailId: string): Promise<OutlookMailProcessingResponse> {
    const url = `${this.baseUrl}/mail-assistent/mails/current?mail_id=${encodeURIComponent(mailId)}`;
    return lastValueFrom(
      this.http.get<OutlookMailProcessingResponse>(url).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  async unsubscribeSubscriptions(): Promise<OutlookUnsubscribeResponse> {
    const url = `${this.baseUrl}/mail-assistent/subscriptions/unsubscribe`;
    return lastValueFrom(
      this.http.post<OutlookUnsubscribeResponse>(url, {}).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  async rejectCurrentMail(mailId: string): Promise<OutlookRejectMailResponse> {
    const url = `${this.baseUrl}/mail-assistent/mails/reject`;
    return lastValueFrom(
      this.http.post<OutlookRejectMailResponse>(url, { mail_id: mailId }).pipe(
        catchError((error: unknown) => {
          console.error('[OutlookMailboxService]', error);
          return throwError(() => error);
        }),
      ),
    );
  }

  messageForMailboxHttpError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'Geen verbinding met de server. Controleer je netwerk.';
      }
      const body = error.error as { message?: string } | null;
      if (body != null && typeof body.message === 'string' && body.message.trim() !== '') {
        return body.message;
      }
      if (error.status === 401) {
        return 'Je sessie is verlopen. Vernieuw het venster of meld opnieuw aan.';
      }
      if (error.status === 422) {
        return 'De aanvraag is onvolledig (ontbreekt Office-token?).';
      }
      if (error.status >= 500) {
        return 'De server is tijdelijk niet beschikbaar.';
      }
      return `Aanvraag mislukt (HTTP ${error.status}).`;
    }
    return 'Er ging iets mis bij mailboxen.';
  }
}
